import { useCallback, useRef, useState, type ReactNode } from "react";

interface ResizablePanelsProps {
  left: ReactNode;
  right: ReactNode;
  defaultLeftPercent?: number;
  minLeftPx?: number;
  minRightPx?: number;
}

export function ResizablePanels({
  left,
  right,
  defaultLeftPercent = 40,
  minLeftPx = 320,
  minRightPx = 300,
}: ResizablePanelsProps) {
  const STORAGE_KEY = "ortvis-panel-split";
  const containerRef = useRef<HTMLDivElement>(null);
  const leftPercentRef = useRef(0);
  const [leftPercent, setLeftPercent] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    const val = saved ? parseFloat(saved) : defaultLeftPercent;
    leftPercentRef.current = val;
    return val;
  });
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);

      const container = containerRef.current;
      if (!container) return;

      const onMouseMove = (moveEvent: MouseEvent) => {
        const rect = container.getBoundingClientRect();
        const totalWidth = rect.width;
        const x = moveEvent.clientX - rect.left;

        // Enforce min widths
        const minLeftPercent = (minLeftPx / totalWidth) * 100;
        const maxLeftPercent = ((totalWidth - minRightPx) / totalWidth) * 100;
        const newPercent = Math.max(minLeftPercent, Math.min(maxLeftPercent, (x / totalWidth) * 100));

        leftPercentRef.current = newPercent;
        setLeftPercent(newPercent);
      };

      const onMouseUp = () => {
        setIsDragging(false);
        localStorage.setItem(STORAGE_KEY, String(leftPercentRef.current));
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [minLeftPx, minRightPx],
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault();
      setIsDragging(true);

      const container = containerRef.current;
      if (!container) return;

      const onTouchMove = (moveEvent: TouchEvent) => {
        moveEvent.preventDefault();
        const touch = moveEvent.touches[0];
        if (!touch) return;
        const rect = container.getBoundingClientRect();
        const totalWidth = rect.width;
        const x = touch.clientX - rect.left;

        const minLeftPercent = (minLeftPx / totalWidth) * 100;
        const maxLeftPercent = ((totalWidth - minRightPx) / totalWidth) * 100;
        const newPercent = Math.max(minLeftPercent, Math.min(maxLeftPercent, (x / totalWidth) * 100));

        leftPercentRef.current = newPercent;
        setLeftPercent(newPercent);
      };

      const onTouchEnd = () => {
        setIsDragging(false);
        localStorage.setItem(STORAGE_KEY, String(leftPercentRef.current));
        document.removeEventListener("touchmove", onTouchMove);
        document.removeEventListener("touchend", onTouchEnd);
      };

      document.addEventListener("touchmove", onTouchMove, { passive: false });
      document.addEventListener("touchend", onTouchEnd);
    },
    [minLeftPx, minRightPx],
  );

  const handleDoubleClick = useCallback(() => {
    leftPercentRef.current = defaultLeftPercent;
    setLeftPercent(defaultLeftPercent);
    localStorage.removeItem(STORAGE_KEY);
  }, [defaultLeftPercent]);

  return (
    <div ref={containerRef} className="flex h-full min-h-0">
      {/* Left panel */}
      <div style={{ width: `${leftPercent}%` }} className="min-w-0 flex-shrink-0">
        {left}
      </div>

      {/* Divider */}
      <div
        className={`cx-divider ${isDragging ? "cx-divider--active" : ""}`}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onDoubleClick={handleDoubleClick}
      />

      {/* Right panel */}
      <div className="min-w-0 flex-1">
        {right}
      </div>
    </div>
  );
}
